import type React from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/user-context';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Define allowed user types for the admin dashboard
const ALLOWED_USER_TYPES = ['admin', 'owner', 'trainer', 'reception', 'staff'];

// Helper function to check if user has admin access
function hasAdminAccess(
  user: { userType?: string; role?: string; isAdmin?: boolean } | null
): boolean {
  if (!user) return false;

  // Check userType (regular users)
  if (user.userType && ALLOWED_USER_TYPES.includes(user.userType)) return true;

  // Check role (tenant admins)
  if (user.role && ALLOWED_USER_TYPES.includes(user.role)) return true;

  // Check isAdmin flag (tenant admin flag)
  if (user.isAdmin === true) return true;

  return false;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isLoading, isAuthenticated } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    console.log('ProtectedRoute: user', user, 'isLoading', isLoading);

    if (!isLoading) {
      // Not authenticated
      if (!isAuthenticated) {
        console.log('Redirecting to login - not authenticated');
        navigate('/login');
        return;
      }

      // Authenticated but not allowed user type
      if (user && !hasAdminAccess(user)) {
        console.log('Redirecting to login - not allowed user type');
        navigate('/login?error=accessDenied');
        return;
      }
    }
  }, [isAuthenticated, isLoading, navigate, user]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !hasAdminAccess(user)) {
    return null;
  }

  return <>{children}</>;
}
